/**
 * InstrumentPlanSidebar - right-rail Instrument Plan list (cardiometabolic
 * + longevity expansion 2026-05-07).
 *
 * Anchored to the prototype's "TLP1 Instrument Plan" sidebar
 * (https://ai-native-prototype.vercel.app/care-pathway-studio.html PROMs
 * tab). The prototype lists the seeded instruments with item count + IP
 * marker; this build uses the Brand-Patch v2 clinical pathway naming
 * ("Cardiometabolic Pathway + Longevity Instrument Plan") and groups
 * the 13 instruments by track so the cardiometabolic + longevity split
 * is obvious at a glance.
 *
 * Each row renders:
 *   - Instrument title.
 *   - Item count (the validated `itemCountTotal`).
 *   - License posture badge (`free`, `licensed`, `Scrips IP`).
 *   - Selection click handler so the row doubles as a navigation chip.
 *
 * HC2 / HC5: token-only, no em / en dashes.
 * HC6 a11y: list rows are real `<button>` elements with explicit
 *   `aria-label` so screen readers announce the title + license posture.
 */
import { useMemo } from 'react';
import {
  PROM_DEFINITIONS,
  type PromDefinition,
  type PromId,
} from './prom-definitions';

export interface InstrumentPlanSidebarProps {
  /** Currently selected PROM id (highlighted in the list). */
  selected: PromId | null;
  /** Click handler. The PROMs view wires this to setSelected. */
  onSelectInstrument: (id: PromId) => void;
  /** Optional class hook for layout-specific overrides. */
  className?: string;
}

const LICENSE_BADGE: Record<
  PromDefinition['licenseStatus'],
  { label: string; bgClass: string; fgClass: string }
> = {
  free: {
    label: 'Free',
    bgClass: 'bg-status-active-bg',
    fgClass: 'text-status-active-fg',
  },
  licensed: {
    label: 'Licensed',
    bgClass: 'bg-warning-soft-bg',
    fgClass: 'text-warning-soft-fg',
  },
  'scrips-ip': {
    label: 'Scrips IP',
    bgClass: 'bg-signal-primary-100',
    fgClass: 'text-signal-primary-700',
  },
};

function InstrumentRow({
  def,
  selected,
  onSelect,
}: {
  def: PromDefinition;
  selected: boolean;
  onSelect: (id: PromId) => void;
}) {
  const badge = LICENSE_BADGE[def.licenseStatus];
  return (
    <li role="listitem" data-prom-plan-row={def.id}>
      <button
        type="button"
        aria-pressed={selected}
        aria-label={`${def.title}, ${def.itemCountTotal} items, ${badge.label}`}
        data-testid={`instrument-plan-row-${def.id}`}
        onClick={() => onSelect(def.id)}
        className={[
          'flex w-full items-center justify-between gap-gutter-sm rounded-signal-md px-gutter-sm py-gutter-sm text-left ring-1 ring-inset transition-colors',
          selected
            ? 'bg-signal-primary-100 text-signal-primary-700 ring-signal-primary-600'
            : 'bg-surface-base text-text-secondary ring-surface-border hover:bg-surface-muted',
        ].join(' ')}
      >
        <span className="flex flex-col">
          <span className="text-signal-sm font-semibold text-text-primary">
            {def.title}
          </span>
          <span className="text-signal-xs text-text-tertiary">
            {def.itemCountTotal} items
            <span> · LOINC </span>
            {def.loincCode}
          </span>
        </span>
        <span
          className={[
            'shrink-0 rounded-signal-pill px-2 py-0.5 text-signal-xs font-semibold uppercase tracking-wide',
            badge.bgClass,
            badge.fgClass,
          ].join(' ')}
        >
          {badge.label}
        </span>
      </button>
    </li>
  );
}

export function InstrumentPlanSidebar({
  selected,
  onSelectInstrument,
  className,
}: InstrumentPlanSidebarProps) {
  const grouped = useMemo(() => {
    const cardio: PromDefinition[] = [];
    const longevity: PromDefinition[] = [];
    for (const def of PROM_DEFINITIONS) {
      if (def.track === 'cardiometabolic') cardio.push(def);
      else longevity.push(def);
    }
    return { cardio, longevity };
  }, []);

  return (
    <aside
      aria-labelledby="instrument-plan-title"
      data-testid="instrument-plan-sidebar"
      className={[
        'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex flex-col gap-1">
        <p className="text-signal-xs uppercase tracking-wide text-text-tertiary">
          Right rail
        </p>
        <h3
          id="instrument-plan-title"
          className="text-signal-base font-semibold text-text-primary"
        >
          Cardiometabolic Pathway + Longevity Instrument Plan
        </h3>
        <p className="text-signal-xs text-text-tertiary">
          {PROM_DEFINITIONS.length} instruments seeded
          <span> · </span>
          {grouped.cardio.length} cardiometabolic
          <span> · </span>
          {grouped.longevity.length} longevity
        </p>
      </header>

      <section
        aria-labelledby="instrument-plan-cardio-title"
        className="flex flex-col gap-gutter-sm"
      >
        <h4
          id="instrument-plan-cardio-title"
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Cardiometabolic
        </h4>
        <ul role="list" className="flex flex-col gap-1">
          {grouped.cardio.map((def) => (
            <InstrumentRow
              key={def.id}
              def={def}
              selected={selected === def.id}
              onSelect={onSelectInstrument}
            />
          ))}
        </ul>
      </section>

      <section
        aria-labelledby="instrument-plan-longevity-title"
        className="flex flex-col gap-gutter-sm"
      >
        <h4
          id="instrument-plan-longevity-title"
          className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Longevity
        </h4>
        <ul role="list" className="flex flex-col gap-1">
          {grouped.longevity.map((def) => (
            <InstrumentRow
              key={def.id}
              def={def}
              selected={selected === def.id}
              onSelect={onSelectInstrument}
            />
          ))}
        </ul>
      </section>
    </aside>
  );
}
